import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CalendarRange, ChevronDown, ChevronRight, Factory, Package, RefreshCw, Wallet } from 'lucide-react';
import { usePermissions } from '@hooks/usePermissions';
import { Card } from '@components/Card';
import { Badge } from '@components/Card';
import { Button } from '@components/Button';
import { SimpleTable, TableCell, TableHeader, TableRow } from '@components/Table';
import { stockAnalyticsService } from '../../services/stockAnalyticsService';
import {
  LEAD_TIME_SOURCE_LABELS,
  PurchasePlanProviderGroup,
  PurchasePlanningResponse,
} from '../../types/stockAnalytics';
import { MetricInfoTooltip } from './MetricInfoTooltip';
import { RationalePanel } from './RationalePanel';
import { HorizonPurchaseOrderModal } from './HorizonPurchaseOrderModal';

const fmtInt = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toLocaleString('pt-BR'));
const fmtDec = (v: number | null | undefined, digits = 1) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { maximumFractionDigits: digits, minimumFractionDigits: 0 });
const fmtCurrency = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDateTime = (v: string | null | undefined) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

const ProviderGroup: React.FC<{ group: PurchasePlanProviderGroup; defaultOpen: boolean }> = ({ group, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  return (
    <Card padding="none">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors rounded-t-xl"
      >
        <div className="flex items-center gap-3">
          {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
          <Factory size={16} className="text-emerald-600" />
          <span className="font-medium text-slate-800">{group.provider_name}</span>
        </div>
        <div className="flex items-center gap-4 text-sm text-slate-600">
          <span>{fmtInt(group.total_items)} itens</span>
          <span>{fmtInt(group.total_qty)} un</span>
          <span className="font-semibold text-slate-800">{fmtCurrency(group.estimated_total)}</span>
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-100">
          <SimpleTable>
            <TableHeader>
              <tr>
                <th className="table-header th w-8"></th>
                <th className="table-header th">SKU</th>
                <th className="table-header th">Descrição</th>
                <th className="table-header th">Físico</th>
                <th className="table-header th"><span className="inline-flex items-center gap-1">Disponível <MetricInfoTooltip metricKey="current_stock_available_qty" /></span></th>
                <th className="table-header th"><span className="inline-flex items-center gap-1">Em pedido <MetricInfoTooltip metricKey="on_order_qty" /></span></th>
                <th className="table-header th"><span className="inline-flex items-center gap-1">Cobertura <MetricInfoTooltip metricKey="coverage_days" /></span></th>
                <th className="table-header th"><span className="inline-flex items-center gap-1">Lead time <MetricInfoTooltip metricKey="lead_time_avg_days" /></span></th>
                <th className="table-header th"><span className="inline-flex items-center gap-1">Demanda prevista <MetricInfoTooltip metricKey="forecast_cycle_qty" /></span></th>
                <th className="table-header th"><span className="inline-flex items-center gap-1">Compra sugerida <MetricInfoTooltip metricKey="suggested_order_qty" /></span></th>
                <th className="table-header th">Preço unit.</th>
                <th className="table-header th">Subtotal</th>
              </tr>
            </TableHeader>
            <tbody>
              {group.items.map((item) => {
                const expanded = expandedId === item.product_id;
                return (
                  <React.Fragment key={item.product_id}>
                    <TableRow>
                      <TableCell className="align-middle">
                        <button
                          onClick={() => setExpandedId(expanded ? null : item.product_id)}
                          className="p-1 rounded hover:bg-slate-100 text-slate-400"
                          title={expanded ? 'Ocultar memória de cálculo' : 'Ver memória de cálculo'}
                          aria-label="Memória de cálculo"
                        >
                          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        <span className="inline-flex items-center gap-1.5">
                          {item.sku}
                          {item.stockout_risk_flag && <Badge variant="danger" size="sm">Ruptura</Badge>}
                        </span>
                      </TableCell>
                      <TableCell><span className="text-sm">{item.description ?? '—'}</span></TableCell>
                      <TableCell>{fmtInt(item.current_stock_qty)}</TableCell>
                      <TableCell>
                        <span className={item.current_stock_available_qty !== item.current_stock_qty ? 'font-medium text-slate-800' : ''}>
                          {fmtInt(item.current_stock_available_qty)}
                        </span>
                      </TableCell>
                      <TableCell>{fmtDec(item.on_order_qty, 0)}</TableCell>
                      <TableCell>
                        {item.coverage_days !== null ? (
                          <span className={item.stockout_risk_flag ? 'text-red-600 font-semibold' : ''}>{fmtDec(item.coverage_days, 0)} d</span>
                        ) : ('—')}
                      </TableCell>
                      <TableCell>
                        {item.lead_time_avg_days !== null ? (
                          <span className="text-xs">
                            {fmtDec(item.lead_time_avg_days, 0)}
                            {item.lead_time_std_dev_days !== null && ` ± ${fmtDec(item.lead_time_std_dev_days, 0)}`} d
                            <span className="block text-[10px] text-slate-400">
                              fonte: {LEAD_TIME_SOURCE_LABELS[item.lead_time_source] ?? '—'}
                            </span>
                          </span>
                        ) : ('—')}
                      </TableCell>
                      <TableCell>{fmtDec(item.forecast_cycle_qty, 0)}</TableCell>
                      <TableCell><span className="font-semibold text-emerald-700">{fmtInt(item.suggested_order_qty)}</span></TableCell>
                      <TableCell>
                        {item.provider_price !== null ? (
                          fmtCurrency(item.provider_price)
                        ) : item.unit_cost !== null ? (
                          <span title="Sem preço de fornecedor cadastrado — usando custo do produto">{fmtCurrency(item.unit_cost)}<span className="text-slate-400">*</span></span>
                        ) : ('—')}
                      </TableCell>
                      <TableCell className="font-medium">{fmtCurrency(item.estimated_value)}</TableCell>
                    </TableRow>
                    {expanded && (
                      <tr className="bg-slate-50">
                        <td colSpan={12} className="px-6 py-3">
                          <p className="text-xs font-semibold text-slate-700 mb-2">Como foi calculado</p>
                          <RationalePanel rationaleJson={item.rationale_json} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </SimpleTable>
          <p className="px-4 py-2 text-[11px] text-slate-400">
            * preço com asterisco usa o custo do cadastro do produto (fornecedor sem preço em Tabela de Preços).
          </p>
        </div>
      )}
    </Card>
  );
};

/**
 * Planejamento de Compras: sugestões de reposição agrupadas por fornecedor,
 * calculadas pelo batch stock_analytics (demanda + sazonalidade + lead time).
 */
export const PurchasePlanningReportView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canCreatePO = checkPermission('purchase_orders.create').hasPermission;
  const [data, setData] = useState<PurchasePlanningResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onlyWithSuggestion, setOnlyWithSuggestion] = useState(true);
  // Fornecedor escolhido (filtra a lista e habilita o pedido por horizonte).
  // As opções vêm da própria resposta sem filtro, então guardamos a lista completa.
  const [providerId, setProviderId] = useState<number | null>(null);
  const [providerOptions, setProviderOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [horizonOpen, setHorizonOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    stockAnalyticsService
      .getPurchasePlanning(providerId, onlyWithSuggestion)
      .then((resp) => {
        setData(resp);
        if (providerId === null) {
          setProviderOptions(
            (resp.providers ?? [])
              .filter((g) => g.provider_id !== null)
              .map((g) => ({ id: g.provider_id as number, name: g.provider_name })),
          );
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar planejamento de compras'))
      .finally(() => setLoading(false));
  }, [onlyWithSuggestion, providerId]);

  useEffect(load, [load]);

  const providers = data?.providers ?? [];
  const selectedProvider = providerOptions.find((p) => p.id === providerId) ?? null;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500">
          Sugestões de reposição por fornecedor, baseadas em demanda histórica, sazonalidade e lead time.
          Última atualização: <span className="font-medium text-slate-700">{fmtDateTime(data?.last_calculated_at)}</span>
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <select
            className="input text-sm"
            value={providerId ?? ''}
            onChange={(e) => setProviderId(e.target.value ? Number(e.target.value) : null)}
            aria-label="Fornecedor"
          >
            <option value="">Todos os fornecedores</option>
            {providerOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={onlyWithSuggestion}
              onChange={(e) => setOnlyWithSuggestion(e.target.checked)}
              className="rounded border-slate-300"
            />
            Somente itens com sugestão de compra
          </label>
          <Button variant="outline" onClick={load}>
            <RefreshCw size={14} className="mr-1.5" /> Atualizar
          </Button>
          {canCreatePO && (
            <Button
              onClick={() => setHorizonOpen(true)}
              disabled={!selectedProvider}
              title={selectedProvider ? 'Pedido de compra cobrindo os próximos X meses deste fornecedor' : 'Escolha um fornecedor'}
            >
              <CalendarRange size={14} className="mr-1.5" /> Pedido para X meses
            </Button>
          )}
        </div>
      </div>

      {selectedProvider && (
        <HorizonPurchaseOrderModal
          isOpen={horizonOpen}
          onClose={() => setHorizonOpen(false)}
          providerId={selectedProvider.id}
          providerName={selectedProvider.name}
        />
      )}

      {/* Cards */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card padding="sm">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100"><Factory size={18} className="text-emerald-700" /></div>
              <div>
                <p className="text-xs text-slate-500">Fornecedores</p>
                <p className="text-xl font-bold text-slate-900">{fmtInt(data.providers_count)}</p>
              </div>
            </div>
          </Card>
          <Card padding="sm">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100"><Package size={18} className="text-blue-700" /></div>
              <div>
                <p className="text-xs text-slate-500">Itens sugeridos</p>
                <p className="text-xl font-bold text-slate-900">{fmtInt(data.items_count)}</p>
              </div>
            </div>
          </Card>
          <Card padding="sm">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100"><Wallet size={18} className="text-amber-700" /></div>
              <div>
                <p className="text-xs text-slate-500">Valor estimado total</p>
                <p className="text-xl font-bold text-slate-900">{fmtCurrency(data.estimated_total)}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {loading && <p className="text-sm text-slate-500">Carregando planejamento de compras…</p>}

      {!loading && !error && providers.length === 0 && (
        <Card>
          <p className="text-sm text-slate-500 text-center py-8">
            Nenhuma sugestão de compra no momento. Se o processo batch ainda não rodou hoje, execute-o na tela de
            Processos; caso contrário, o estoque atual + pedidos abertos já cobrem a demanda prevista.
          </p>
        </Card>
      )}

      <div className="space-y-3">
        {providers.map((group, index) => (
          <ProviderGroup key={group.provider_id ?? 'none'} group={group} defaultOpen={index === 0} />
        ))}
      </div>
    </div>
  );
};

export default PurchasePlanningReportView;
